import React, { useEffect, useState } from "react";
import toast, { Toaster } from 'react-hot-toast';
import { Link, useLocation } from "react-router-dom";
import FullLogo from './images/DAFullLogo.png'
import OnlyLogo from './images/OnlyLogo.png'
import { useSelector } from "react-redux"
import { selectUser } from 'src/stores/userSlice';
import Tooltip from '@mui/material/Tooltip';
import {
  LayoutDashboard,
  Shield,
  ShieldCheck,
  Network,
  ClipboardPenLine,
  CalendarDays,
  Users,
  CircleHelp,
  FileText,
  ChevronsRight,
  ChevronsLeft
} from "lucide-react";

const darkToast = () =>
  toast("Please submit your Daily status report for the yesterday", {
    duration: "1000",
    style: {
      borderRadius: "12px",
      background: "#90185f",
      color: "#fff",
    },
    position: "bottom-right",
  });

const SidebarItem = ({
  to,
  Icon,
  label,
  isActive,
  isCollapsed,
  setIsMobileMenuOpen
}) => (
  <li className="w-full">
    <Tooltip
      title={label}
      placement="right"
      arrow
      disableHoverListener={!isCollapsed}
      classes={{ tooltip: 'bg-slate-800 text-xs py-1.5 px-3 rounded-md shadow-lg' }}
    >
      <Link
        to={to}
        onClick={() => setIsMobileMenuOpen && setIsMobileMenuOpen(false)}
        className={`block w-full py-[3px] ${
          isCollapsed ? "px-2" : "px-3"
        }`}
      >
        <div
          className={`
            group relative flex h-[44px] items-center rounded-xl
            transition-all duration-300 ease-out overflow-hidden
            ${
              isCollapsed
                ? "justify-center"
                : "gap-3.5 px-3.5"
            }
            ${
              isActive
                ? "bg-gradient-to-r from-blue-50/80 to-transparent text-blue-700 font-semibold shadow-[inset_1px_0_0_rgba(255,255,255,1)]"
                : "text-slate-600 hover:bg-slate-50/80 hover:text-slate-900"
            }
          `}
        >
          {isActive && (
            <span className="absolute left-0 h-6 w-1 rounded-r-full bg-blue-600 shadow-[0_0_8px_rgba(37,99,235,0.4)]" />
          )}

          <Icon
            size={18}
            strokeWidth={isActive ? 2.2 : 1.8}
            className={`shrink-0 transition-all duration-300 ${
              isActive
                ? "text-blue-600"
                : "text-slate-500 group-hover:text-slate-700 group-hover:scale-110"
            }`}
          />

          {!isCollapsed && (
            <span
              className={`truncate text-[14px] tracking-tight transition-transform duration-300 ease-out ${
                isActive ? "font-semibold" : "font-medium group-hover:translate-x-0.5"
              }`}
            >
              {label}
            </span>
          )}
        </div>
      </Link>
    </Tooltip>
  </li>
);

const Sidebar = ({ isMobileMenuOpen, setIsMobileMenuOpen }) => {
  const user = useSelector(selectUser)
  const [empCode, setEmpCode] = useState("");
  const [isCollapsed, setIsCollapsed] = useState(false);
  const location = useLocation();
  const Kaitongo = process.env.REACT_APP_KAITONGO_MANAGER?.split(',') || [];
  
  useEffect(() => {
    if (user !== null) {
      if (Kaitongo.includes(user.emp_code)) {
        setEmpCode(user.emp_code);
      } else {
        setEmpCode("");
      }
    }
  }, [user])

    const checkActive = (path) => location.pathname.includes(path);
    const menuItems = [
    {
      to: "super-admin",
      icon: Shield,
      label: "Super-Admin",
      show: user?.role === "Admin",
      category: "Administration",
    },
    {
      to: "admin",
      icon: ShieldCheck,
      label: "Manager",
      show: user?.role === "Manager",
      category: "Administration",
    },
    {
      to: "hr-panel",
      icon: Network,
      label: "HR-Panel",
      show: user?.role === "HR",
      category: "Administration",
    },
    {
      to: "dashboard",
      icon: LayoutDashboard,
      label: "Dashboard",
      show: true,
      category: "Workspace",
    },
    {
      to: "daily-status",
      icon: ClipboardPenLine,
      label: "Daily Status",
      show: true,
      category: "Workspace",
    },
    {
      to: "leave",
      icon: CalendarDays,
      label: "Leave",
      show: empCode === "",
      category: "Workspace",
    },
    {
      to: "team",
      icon: Users,
      label: "Our-Team",
      show: true,
      category: "Resources",
    },
    {
      to: "queries",
      icon: CircleHelp,
      label: "Queries",
      show: true,
      category: "Resources",
    },
    {
      to: "policies",
      icon: FileText,
      label: "Policies",
      show: true,
      category: "Resources",
    },
  ];

  // Group items by category
  const groupedItems = menuItems.reduce((acc, item) => {
    if (item.show) {
      if (!acc[item.category]) acc[item.category] = [];
      acc[item.category].push(item);
    }
    return acc;
  }, {});

  const categoryOrder = ["Administration", "Workspace",  "Resources"];
  return (
    <aside className={`fixed left-0 top-[60px] md:top-0 w-[260px] md:relative flex flex-col items-center h-[calc(100dvh-60px)] md:h-screen ${isCollapsed ? 'md:w-20' : 'md:w-64'} 
      transition-all duration-300 ease-[cubic-bezier(0.4,0,0.2,1)]
      ${isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'}
      bg-white text-slate-700 flex-shrink-0 z-40 md:border-r border-slate-200/60 shadow-[4px_0_24px_rgba(0,0,0,0.02)] md:shadow-[4px_0_24px_rgba(0,0,0,0.02)]`}
    >
      
      {/* Toggle Button (Hidden on Mobile) */}
      <button 
        onClick={() => setIsCollapsed(!isCollapsed)}
        className="hidden md:flex absolute -right-3.5 top-1/2 -translate-y-1/2 w-7 h-7 bg-white border border-slate-200 rounded-full items-center justify-center shadow-[0_2px_8px_rgba(0,0,0,0.08)] hover:shadow-[0_4px_12px_rgba(0,0,0,0.12)] hover:border-slate-300 text-slate-400 hover:text-blue-600 z-50 transition-all duration-300 cursor-pointer group"
        title={isCollapsed ? "Expand Sidebar" : "Collapse Sidebar"}
      >
        {isCollapsed ? <ChevronsRight className="w-4 h-4 group-hover:scale-110 transition-transform" /> : <ChevronsLeft className="w-4 h-4 group-hover:scale-110 transition-transform" />}
      </button>

      {/* Header / Logo (Hidden on Mobile since it's in Navbar now) */}
      <div className={`hidden md:flex items-center justify-center h-20 border-b border-slate-100 ${isCollapsed ? 'p-2' : 'p-4'} w-full bg-white/50 backdrop-blur-sm`}>
        <a href="/" className="transition-transform hover:scale-105 flex justify-center items-center h-full w-full">
          <img src={isCollapsed ? OnlyLogo : FullLogo} alt="Data Aces" className={`max-h-10 object-contain transition-all duration-300 ${isCollapsed ? 'max-w-[40px]' : 'w-[140px]'}`} />
        </a>
      </div>

      {/* Menu Content */}
      <div className={`flex-1 overflow-y-auto py-5 w-full ${isCollapsed ? 'px-2' : 'px-3'} sidebar-content overflow-x-hidden`}>
        {categoryOrder.map(category => {
          if (!groupedItems[category]) return null;
          return (
            <div key={category} className="mb-6 last:mb-2">
              {!isCollapsed && (
                <div className="px-4 mb-2">
                  <h4 className="text-[10px] font-bold tracking-widest text-slate-400 uppercase">
                    {category}
                  </h4>
                </div>
              )}
              {isCollapsed && (
                <div className="flex justify-center mb-2">
                  <div className="w-4 h-[1px] bg-slate-200 rounded-full" />
                </div>
              )}
              <ul className="space-y-0.5">
                {groupedItems[category].map(({ to, icon, label }) => (
                  <SidebarItem
                    key={to}
                    to={to}
                    Icon={icon}
                    label={label}
                    isActive={checkActive(to)}
                    isCollapsed={isCollapsed}
                    setIsMobileMenuOpen={setIsMobileMenuOpen}
                  />
                ))}
              </ul>
            </div>
          );
        })}
      </div>

      {/* Footer */}
      {!isCollapsed && (
        <div className="p-4 border-t border-slate-100 flex justify-center">
          <Link to="/" className="transition-transform hover:scale-110">
            <img src={OnlyLogo} alt="Data Aces" className="h-8 w-auto object-contain opacity-70 hover:opacity-100 transition-opacity" />
          </Link>
        </div>
      )}
      
      <Toaster position="bottom-right" />
    </aside>
  );
}

export default Sidebar;
